import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AlertTriangle, ShoppingCart, X, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ExpiryWarning = () => {
  const { user, isAdmin, daysUntilExpiry, isExpiringSoon } = useAuth();
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();

  // Debug: verificar valores
  console.log('🔍 ExpiryWarning Debug:', { 
    hasUser: !!user, 
    isAdmin, 
    daysUntilExpiry, 
    isExpiringSoon, 
    isVisible 
  });

  if (!user || isAdmin || !isExpiringSoon || !isVisible) return null;

  const getUrgencyLevel = () => {
    if (daysUntilExpiry <= 1) return 'critical';
    if (daysUntilExpiry <= 2) return 'warning';
    return 'info';
  };

  const urgencyLevel = getUrgencyLevel();
  
  const getColors = () => {
    switch (urgencyLevel) {
      case 'critical':
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          text: 'text-red-800 dark:text-red-200',
          subtext: 'text-red-600 dark:text-red-400',
          button: 'bg-red-600 hover:bg-red-700',
          icon: 'text-red-600 dark:text-red-400'
        };
      case 'warning':
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          border: 'border-orange-200 dark:border-orange-800',
          text: 'text-orange-800 dark:text-orange-200',
          subtext: 'text-orange-600 dark:text-orange-400',
          button: 'bg-orange-600 hover:bg-orange-700',
          icon: 'text-orange-600 dark:text-orange-400'
        };
      default:
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          text: 'text-blue-800 dark:text-blue-200',
          subtext: 'text-blue-600 dark:text-blue-400',
          button: 'bg-blue-600 hover:bg-blue-700',
          icon: 'text-blue-600 dark:text-blue-400'
        };
    }
  };

  const colors = getColors();

  return (
    <div className={`${colors.bg} border-b ${colors.border}`}>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className={`w-10 h-10 rounded-full ${colors.bg} border-2 ${colors.border} flex items-center justify-center`}>
              {urgencyLevel === 'critical' ? (
                <AlertTriangle className={`w-5 h-5 ${colors.icon}`} />
              ) : (
                <Clock className={`w-5 h-5 ${colors.icon}`} />
              )}
            </div>
            <div>
              <p className={`text-sm font-semibold ${colors.text}`}>
                {urgencyLevel === 'critical' ? 'ℹ️ INFORMAÇÃO: ' : 'ℹ️ LEMBRETE: '}
                Sua licença expira em {daysUntilExpiry} dia{daysUntilExpiry !== 1 ? 's' : ''}
              </p>
              <p className={`text-xs ${colors.subtext} mt-1`}>
                {urgencyLevel === 'critical' 
                  ? 'Para manter acesso contínuo, renove sua licença'
                  : 'Considere renovar para evitar interrupções no serviço'
                }
              </p>
              {user.key_expires_at && (
                <p className={`text-xs ${colors.subtext} mt-1`}>
                  Data de expiração: {
                    typeof user.key_expires_at === 'string' 
                      ? new Date(user.key_expires_at).toLocaleDateString('pt-BR') 
                      : user.key_expires_at.toLocaleDateString('pt-BR')
                  } às {
                    typeof user.key_expires_at === 'string'
                      ? new Date(user.key_expires_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                      : user.key_expires_at.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                  }
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate('/checkout')}
              className={`inline-flex items-center space-x-2 px-4 py-2 ${colors.button} text-white text-sm font-medium rounded-lg transition-colors`}
            >
              <ShoppingCart className="w-4 h-4" />
              <span>Renovar Licença</span>
            </button>
            
            <button
              onClick={() => setIsVisible(false)}
              className={`p-2 ${colors.subtext} hover:${colors.text} rounded-lg transition-colors`}
              title="Fechar aviso"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Barra de progresso para mostrar urgência */}
        <div className="mt-3">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                urgencyLevel === 'critical' ? 'bg-red-500' :
                urgencyLevel === 'warning' ? 'bg-orange-500' : 'bg-blue-500'
              }`}
              style={{ width: `${Math.max(10, (8 - daysUntilExpiry) * 12.5)}%` }}
            ></div>
          </div>
          <p className={`text-xs ${colors.subtext} mt-1 text-center`}>
            {urgencyLevel === 'critical' ? 'Licença próxima ao vencimento' : 
             urgencyLevel === 'warning' ? 'Renovação sugerida' : 'Renovação recomendada'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExpiryWarning;